import type { Shareholder } from "~/types/company";

export interface OwnershipSplit {
	/** Share held by physical persons (fyzické osoby), 0–100. */
	personsPct: number;
	/** Share held by legal entities (právnické osoby), 0–100. */
	entitiesPct: number;
}

function round1(value: number): number {
	return Math.round(value * 10) / 10;
}

/**
 * Computes the persons-vs-entities ownership split from active shareholders.
 * Per-shareholder share = `ownershipPercentage` when set, otherwise
 * `shareAmount / Σ(shareAmounts) * 100`. Returns null when nothing is computable.
 */
export function computeOwnershipSplit(shareholders: Shareholder[]): OwnershipSplit | null {
	const active = shareholders.filter((s) => s.isActive);
	if (active.length === 0) return null;

	const totalShareAmount = active.reduce(
		(sum, s) => sum + (s.shareAmount != null && s.shareAmount > 0 ? s.shareAmount : 0),
		0,
	);

	let personsPct = 0;
	let entitiesPct = 0;
	let computable = false;

	for (const sh of active) {
		let share: number | null = null;
		if (sh.ownershipPercentage != null) {
			share = sh.ownershipPercentage;
		} else if (sh.shareAmount != null && sh.shareAmount > 0 && totalShareAmount > 0) {
			share = (sh.shareAmount / totalShareAmount) * 100;
		}
		if (share == null) continue;
		computable = true;

		const isPerson = sh.person != null || sh.personId != null;
		if (isPerson) {
			personsPct += share;
		} else {
			entitiesPct += share;
		}
	}

	if (!computable) return null;

	// Guard against rounding/duplicate-data overflow — the bar must stay within 100 %.
	const total = personsPct + entitiesPct;
	if (total > 100) {
		personsPct = (personsPct / total) * 100;
		entitiesPct = (entitiesPct / total) * 100;
	}

	return { personsPct: round1(personsPct), entitiesPct: round1(entitiesPct) };
}
